/** GPS overlay lock: tight GNSS vs coarse fused-location accept.
 *
 * Geolocation session rules (not wishes):
 * - `enableHighAccuracy` is a hint the UA may ignore (W3C).
 * - WebKit ORs it across every watcher on the page, then sets
 *   CLLocationManager desiredAccuracy to Best vs HundredMeters.
 *   A parallel `enableHighAccuracy: false` getCurrentPosition is not a
 *   cell/Wi-Fi channel once a high-acc watch exists.
 * - `timeout` on getCurrentPosition / watchPosition is a TIMEOUT error.
 *   Safari can abort the shared session. The app owns 8 s indoor / 25 s
 *   coarse-accept. Never pass `timeout` into the Geolocation API.
 * - A 25 s *app* interval that clearWatch + watchPosition is the same
 *   abort. Timer-revive is forbidden (`shouldReviveWatch` reason
 *   `watchdog` is always false).
 * - `maximumAge` is how a cached CoreLocation sample (the Maps blue dot)
 *   arrives as the first callback instead of waiting for GNSS.
 * - W3C `watchPosition` returns 0 when the document is not fully active.
 *   0 is a failure, not a live watch.
 * - iOS Safari suspends the watch in the background. Re-subscribe on
 *   visibility/pageshow when the watch is missing, or the document was
 *   hidden ≥ GPS_BACKGROUND_MS (real background, not a shade flick).
 *   Always after bfcache, except when this watch was armed < 2 s ago
 *   (pageshow + visibility both fire; the second must not abort TTFF).
 *   Do not use callback silence — a canopy gap plus a notification
 *   shade would abort a live search. LiveView reconnect must not touch
 *   a live watch. Overlay lock dies if silent > maximumAge (60 s).
 * - `Number(null)` is 0. Never coerce accuracy or lat/lng before
 *   `gpsAccuracyM` / `gpsCoord`.
 * - Samples > 80 m never seed Kalman / `_bestAcc` / overlay, even first.
 * - After a watch restart, the next good sample is `seed()`, not
 *   `updateGps()`. still() would drop a walk against the frozen origin.
 *   That sample must be fresh at receipt (≤ GPS_FRESH_MS). A 40 s
 *   `maximumAge` cache is where they were.
 * - `clearWatch` the raw `watchPosition` return, not the coerced number.
 * - `clearWatch` does not cancel an in-flight success/error. `stop()`
 *   drops later callbacks (`live = false`) before `clearWatch`.
 * - Some WebViews report `timestamp` in seconds. Convert before age.
 * - `permissions.query` then/change must not start a watch after teardown.
 */

export const GPS_TIGHT_M = 20
export const GPS_COARSE_MAX_M = 80
export const GPS_COARSE_WAIT_MS = 25_000
export const GPS_INDOOR_WAIT_MS = 8_000
export const GPS_INDOOR_STUCK_M = 22
export const GPS_INDOOR_NO_IMPROVE_MS = 5_000
export const GPS_CACHE_MAX_AGE_MS = 60_000
/** Tight lock only for a sample this fresh at receipt. A 60 s cache at 12 m is a walk. */
export const GPS_FRESH_MS = 5_000
/** Real background vs notification-shade flicker. Not a GNSS timeout. */
export const GPS_BACKGROUND_MS = 2_000
/** Standing still, for the silent-hold rule. Same threshold the QA trace calls `still`. */
export const GPS_STILL_MPS = 0.45

export const SKY_WAIT_NEAR =
  "No sky view yet — waiting for an approximate lock"

/** Overlay is already up on a coarse lock. Do not say "go outside" (canopy roofs). */
export const SKY_WAIT_AIM =
  "No sky view — hologram may sit off. Open sky tightens it."

/** Persistent sky copy once the hologram is on an approximate lock. */
export function gpsSkyHint({overlayOk, approximate} = {}) {
  if (overlayOk && approximate) return SKY_WAIT_AIM
  return null
}

/**
 * When the HUD should say the position is not updating.
 *
 * Twice the fresh window, so one dropped callback does not flash a warning at a
 * healthy walk (the corpus shows receipt ages of 4–236 ms on good walks). Above
 * this the fan is looking at a hologram anchored to where they *were*
 * (`docs/todo/20261017_qa_gps_frozen_hud.md`).
 */
export const GPS_STALE_HUD_MS = 10_000

/** HUD suffix. Says what is wrong, not why. */
export const GPS_STALE_HUD = "not updating"

export function gpsStale({ageMs} = {}) {
  const age = Number.isFinite(ageMs) ? Math.max(0, ageMs) : Infinity
  return age >= GPS_STALE_HUD_MS
}

/** Single watch. No timeout. Cache age so a fused last-known can arrive. */
export const GEO_WATCH_OPTS = {
  enableHighAccuracy: true,
  maximumAge: GPS_CACHE_MAX_AGE_MS,
}

export function gpsLikelyIndoors({accuracy, elapsedMs, bestAcc, sinceImproveMs}) {
  if (!gpsUsableAccuracy(accuracy)) return false
  if (accuracy <= 15) return false
  const stuckCoarse = (bestAcc ?? accuracy) > GPS_INDOOR_STUCK_M
  return (
    elapsedMs > GPS_INDOOR_WAIT_MS &&
    stuckCoarse &&
    sinceImproveMs > GPS_INDOOR_NO_IMPROVE_MS
  )
}

export function gpsUsableAccuracy(accuracy) {
  return Number.isFinite(accuracy) && accuracy >= 0
}

/** Coerce a Geolocation `coords.accuracy`. `Number(null)` is 0 — a fake tight lock. */
export function gpsAccuracyM(value) {
  if (value == null || value === "") return NaN
  const n = typeof value === "number" ? value : Number(value)
  return gpsUsableAccuracy(n) ? n : NaN
}

/** Coerce a Geolocation lat/lng. `Number(null)` is 0 (Null Island). */
export function gpsCoord(value) {
  if (value == null || value === "") return NaN
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

export function gpsCoordsOk(lat, lng) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  )
}

/** Epoch ms. Some WebViews used seconds since epoch (~1e9 vs ~1e12). */
export function gpsPositionTimestampMs(timestamp) {
  if (!Number.isFinite(timestamp)) return NaN
  return timestamp < 1e11 ? timestamp * 1000 : timestamp
}

/**
 * Age of a GeolocationPosition at receipt.
 *
 * This is a **snapshot**: it answers "how old was this sample when it landed".
 * Do not use it as the current age of the fix — it does not advance. For that,
 * see `gpsLiveAgeMs/2`.
 */
export function gpsSampleAgeMs(timestamp, now = Date.now()) {
  const ts = gpsPositionTimestampMs(timestamp)
  if (!Number.isFinite(ts)) return Infinity
  return Math.max(0, now - ts)
}

/**
 * Current age of the fix the viewer is holding.
 *
 * `receiptAgeMs` is what `gpsSampleAgeMs/2` returned when the sample landed;
 * `sinceReceiptMs` is how long ago that was. Their sum is the age right now.
 *
 * Why this exists: `_fixAgeMs` used to be the receipt age alone, so on a watch
 * that stopped calling back the payload said "2.3 s old" forever while
 * `silent_ms` climbed past 112 s (field corpus, 2026-10-20 20:40 and
 * 2026-10-21 09:51). Nothing that reads the age could tell the fix was stale,
 * and a "GPS frozen" HUD warning had no field to key on.
 */
export function gpsLiveAgeMs(receiptAgeMs, sinceReceiptMs) {
  const receipt = Number.isFinite(receiptAgeMs) ? Math.max(0, receiptAgeMs) : Infinity
  if (!Number.isFinite(receipt)) return Infinity
  const since = Number.isFinite(sinceReceiptMs) ? Math.max(0, sinceReceiptMs) : 0
  return receipt + since
}

/** After a watch restart, only a fresh sample may become the new origin. */
export function gpsSeedSampleOk(ageMs) {
  const age = Number.isFinite(ageMs) ? Math.max(0, ageMs) : Infinity
  return age <= GPS_FRESH_MS
}

/** Drop every sample worse than 80 m, including the first. A first 200 m
 *  sample must not seed the Kalman origin; a later 40 m canopy fix is then
 *  a still()-dropped jump. Keep 30–80 m. */
export function gpsIgnoreSpike(acc, _bestAcc) {
  if (!gpsUsableAccuracy(acc)) return true
  return acc > GPS_COARSE_MAX_M
}

/** Live Geolocation sample this session. Seeded last-fix never opens the overlay.
 *  Tight GNSS must be fresh at receipt; a stale cache uses the 25 s coarse ladder. */
/** Coarse accept at the venue is immediate: the fan is already standing there. */
export const GPS_VENUE_ACCEPT_M = 80

/**
 * How long a tight fix may outlive the watch.
 *
 * The 60 s silence rule exists so a stale fix cannot pin a hologram to a wall
 * the fan walked away from. But silence is not movement: three corpus walks
 * lost the overlay at 59-60 s with `acc` 2.9-7.4 m and `spd` 0
 * (docs/reviews/20261021_qa_corpus_analysis.md §4), one of them for 51.9 s of
 * a 111 s walk. Below this multiple of the cache age we keep the overlay on a
 * tight, still fix; past it we accept that we no longer know where the fan is.
 */
export const GPS_SILENT_HOLD_MULT = 2

export function gpsFixOk({
  accuracy,
  liveGps,
  elapsedMs,
  ageMs = Infinity,
  silentMs = 0,
  venue = false,
  speedMps = null,
  tightSilentHoldMs = GPS_CACHE_MAX_AGE_MS * GPS_SILENT_HOLD_MULT,
}) {
  if (!gpsUsableAccuracy(accuracy)) return false
  if (!liveGps) return false
  const silent = Number.isFinite(silentMs) ? Math.max(0, silentMs) : 0
  const age = Number.isFinite(ageMs) ? Math.max(0, ageMs) : Infinity
  const tight = accuracy <= GPS_TIGHT_M
  // A silent watch with a tight and still fix is a working AR view: the fan is
  // standing at the pin and the phone stopped calling back. Hold it, bounded.
  // Absent speed is NOT stillness — only a positive reading extends the hold.
  const still =
    speedMps != null && Number.isFinite(speedMps) && Math.abs(speedMps) < GPS_STILL_MPS
  const heldSilence = tight && still && silent <= tightSilentHoldMs
  if (!heldSilence && silent > GPS_CACHE_MAX_AGE_MS) return false
  if (tight && age <= GPS_FRESH_MS) return true
  // A held sample is old by definition; it is trusted because it is tight and
  // nothing says the fan moved.
  if (heldSilence) return true
  // Inside the venue ring a fresh coarse fix is enough: the 25 s ladder exists
  // to keep a street viewer from seeing a hologram kilometres away, and that
  // risk is gone once you are at the pin.
  if (venue && accuracy <= GPS_VENUE_ACCEPT_M && age <= GPS_FRESH_MS) return true
  return elapsedMs >= GPS_COARSE_WAIT_MS && accuracy <= GPS_COARSE_MAX_M
}

export function gpsHudApproximate({
  accuracy,
  liveGps,
  elapsedMs,
  ageMs = Infinity,
  silentMs = 0,
  venue = false,
  speedMps = null,
} = {}) {
  if (!gpsFixOk({accuracy, liveGps, elapsedMs, ageMs, silentMs, venue, speedMps})) return false
  const age = Number.isFinite(ageMs) ? Math.max(0, ageMs) : Infinity
  if (accuracy > GPS_TIGHT_M || age > GPS_FRESH_MS) return true
  const silent = Number.isFinite(silentMs) ? Math.max(0, silentMs) : 0
  return silent >= GPS_COARSE_WAIT_MS
}

export function coerceWatchId(id) {
  if (typeof id === "bigint") {
    const n = Number(id)
    return Number.isSafeInteger(n) && n > 0 ? n : 0
  }
  const n = typeof id === "number" ? id : Number(id)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export function usableWatchId(id) {
  return coerceWatchId(id) > 0
}

/**
 * iOS Safari shows "Allow Once / While Using / Never" only from a user
 * gesture. A last-fix or sessionReady flag is not a gesture: starting the
 * watch on mount either reuses a grant (no sheet) or silently fails after
 * "Allow Once" expires, and Start exploring then no-ops because a watch id
 * already exists. Prewarm only when the UA already granted.
 */
export function shouldPrewarmWatch(permState) {
  return permState === "granted"
}

/** Skip the Start exploring tap only when this tab already ran AR *and* location is already granted. */
export function shouldSkipArGate({sessionReady, permState}) {
  return Boolean(sessionReady) && permState === "granted"
}

/**
 * Whether to clearWatch + watchPosition again.
 * Timer-revive is the Geolocation `timeout` in disguise.
 * Callback silence is the same abort: canopy samples can be 30–40 s
 * apart, and a notification shade then looks like a dead watch.
 * Foreground revive of a live watch only after the document was actually
 * hidden ≥ GPS_BACKGROUND_MS. Bfcache restarts a stale watch. A watch
 * armed < GPS_BACKGROUND_MS ago is never restarted — pageshow and
 * visibility visible both fire; the second would abort TTFF.
 */
export function shouldReviveWatch({
  denied = false,
  evenIfDenied = false,
  hasWatch = false,
  hiddenMs = 0,
  armedMs = Infinity,
  reason = "foreground",
} = {}) {
  if (denied && !evenIfDenied) return false
  if (reason === "watchdog") return false
  const armed = Number.isFinite(armedMs) ? Math.max(0, armedMs) : Infinity
  if (hasWatch && armed < GPS_BACKGROUND_MS) return false
  if (denied && evenIfDenied) return true
  if (reason === "reconnect") return !hasWatch
  if (reason === "bfcache") return true
  if (!hasWatch) return true
  const hidden = Number.isFinite(hiddenMs) ? Math.max(0, hiddenMs) : 0
  return hidden >= GPS_BACKGROUND_MS
}

/** One watchPosition. Never getCurrentPosition. Caller owns apply/HUD.
 *  `stop()` is the session lifetime: later UA callbacks are no-ops. */
export function startGeolocation(geo, {onFix, onError} = {}) {
  if (!geo || typeof geo.watchPosition !== "function") {
    onError?.({code: 2, message: "unsupported"})
    return {watchId: null, stop() {}}
  }

  let live = true
  let rawId
  const stop = () => {
    if (!live) return
    live = false
    if (rawId == null || rawId === 0) return
    if (typeof geo.clearWatch !== "function") return
    try {
      geo.clearWatch(rawId)
    } catch {
      // Junk id we already rejected, or a UA that throws on double-clear.
    }
  }

  try {
    rawId = geo.watchPosition(
      (pos) => {
        if (!live) return
        onFix?.(pos)
      },
      (err) => {
        if (!live) return
        onError?.(err)
      },
      GEO_WATCH_OPTS
    )
  } catch (err) {
    stop()
    onError?.({code: 2, message: String(err?.message || err)})
    return {watchId: null, stop}
  }

  const watchId = coerceWatchId(rawId)
  if (!watchId) {
    stop()
    return {watchId: null, stop}
  }

  return {watchId, stop}
}
